import React, { useEffect } from 'react';
// FIX: Changed import path for ColorSuggestion to types.ts
import { ColorSuggestion } from '../types';
import { DraggableItem } from './SettingsManager';

interface ColorSuggestionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    suggestions: ColorSuggestion[];
    currentItems: DraggableItem[];
}

const ColorSwatch: React.FC<{ color: string }> = ({ color }) => (
    <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-full border border-gray-300" style={{ backgroundColor: color }}></span>
        <span className="font-mono text-xs">{color}</span>
    </div>
);

export const ColorSuggestionModal: React.FC<ColorSuggestionModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    suggestions,
    currentItems,
}) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                onConfirm();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, onConfirm]);

    if (!isOpen) return null;

    const itemsWithSuggestions = currentItems.map(item => {
        const suggestion = suggestions.find(s => s.name === item.name);
        return {
            ...item,
            newColor: suggestion?.color,
        };
    });

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="suggestion-modal-title"
        >
            <div
                className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full max-h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center pb-3 border-b">
                    <h2 id="suggestion-modal-title" className="text-xl font-bold text-gray-800">Návrh barev od AI</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="my-4 overflow-y-auto">
                    <p className="text-sm text-gray-600 mb-4">
                        Umělá inteligence navrhla následující barevnou paletu. Zkontrolujte prosím návrhy a potvrďte, pokud je chcete aplikovat.
                    </p>
                    <div className="border rounded-md overflow-hidden">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase">Položka</th>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase">Současná barva</th>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase">Nový návrh</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {itemsWithSuggestions.map(item => (
                                    <tr key={item.id}>
                                        <td className="py-3 px-4 text-sm font-medium text-gray-800">{item.name}</td>
                                        <td className="py-3 px-4">
                                            {item.color && <ColorSwatch color={item.color} />}
                                        </td>
                                        <td className="py-3 px-4">
                                            {item.newColor ? <ColorSwatch color={item.newColor} /> : <span className="text-gray-400 text-sm">-</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Zrušit
                    </button>
                    <button onClick={onConfirm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        Přijmout návrh
                    </button>
                </div>
            </div>
        </div>
    );
};